/**
 * User beta features (from GET user/beta-feature, held in
 * `state.auth.betaFeaturesAllowedList`).
 *
 * Cached outside Redux so non-React callers (e.g. getPatientPage) can read
 * the current list without importing the store. The auth slice keeps this in
 * sync whenever `betaFeaturesAllowedList` is dispatched (populateAppData on
 * login, and the Settings beta toggle).
 */

/** Beta feature gating the redesigned `/patient/` profile page. */
export const PATIENT_PAGE_V2_FEATURE = "patient-page-v2";

/**
 * Beta feature gating the new 2026 ("2.0") UI. Enabled for a session only when
 * BOTH the current user AND the selected clinic have this feature (see
 * {@link usesCip20Ui}).
 */
export const CIP_2_0_UI_FEATURE = "cip2.0-ui";

let cachedBetaFeatures: string[] = [];
/**
 * Clinic-scoped beta features for the selected clinic. Kept separate from the
 * user-scoped {@link cachedBetaFeatures} so callers can compute intersections
 * (a feature the user AND the clinic both have) rather than the merged union.
 */
let cachedClinicBetaFeatures: string[] = [];

export const syncBetaFeatures = (features?: readonly string[] | null): void => {
  cachedBetaFeatures = Array.isArray(features) ? [...features] : [];
};

export const syncClinicBetaFeatures = (
  features?: readonly string[] | null
): void => {
  cachedClinicBetaFeatures = Array.isArray(features) ? [...features] : [];
};

export const getBetaFeatures = (): string[] => [...cachedBetaFeatures];

export const getClinicBetaFeatures = (): string[] => [
  ...cachedClinicBetaFeatures,
];

/** True when the user has the `patient-page-v2` beta feature. */
export const usesPatientPageV2 = (): boolean =>
  cachedBetaFeatures.includes(PATIENT_PAGE_V2_FEATURE);

/**
 * True only when BOTH the user and the selected clinic have the `cip2.0-ui`
 * beta feature. Non-React reader backed by the caches above; React components
 * should prefer the `useUse2026UI` hook (also applies the Compassus override).
 */
export const usesCip20Ui = (): boolean =>
  cachedBetaFeatures.includes(CIP_2_0_UI_FEATURE) &&
  cachedClinicBetaFeatures.includes(CIP_2_0_UI_FEATURE);

/**
 * Whether the selected clinic is in Compassus mode (`clinic_type ===
 * "compassus"` or HH-RPM), which never gets the 2026 UI. Kept in sync by the
 * clinic slice's `setUserClinic` so dependency-free callers can apply the same
 * override as `useUse2026UI` / `isUse2026UIEnabled`.
 */
let cachedClinicIsCompassusMode = false;

export const syncSelectedClinicMode = (
  clinic?: {
    clinic_type?: string | null;
    config?: { isHomeHealthRPM?: boolean | null } | null;
  } | null
): void => {
  cachedClinicIsCompassusMode =
    clinic?.clinic_type === "compassus" ||
    clinic?.config?.isHomeHealthRPM === true;
  cachedHasSelectedClinic = !!clinic;
};

/**
 * Whether this session's selected clinic is known yet. The internal 2026-UI
 * launch override must stay OFF until it is, so an employee whose clinic turns
 * out to be Compassus/HH-RPM never briefly sees the 2026 UI during the bootstrap
 * window (email loads before the clinic does). Mirrors the reactive hook's
 * `!!userClinic || clinicBetaFeaturesLoaded`:
 *   - `cachedHasSelectedClinic`: a real clinic was selected (persisted, so it
 *     rehydrates fast on repeat logins — the "first login only" cost).
 *   - `cachedClinicBetaFeaturesLoaded`: the clinic beta features resolved even
 *     when there is no clinic to load them for.
 */
let cachedHasSelectedClinic = false;
let cachedClinicBetaFeaturesLoaded = false;

export const syncClinicBetaFeaturesLoaded = (loaded?: boolean | null): void => {
  cachedClinicBetaFeaturesLoaded = loaded === true;
};

const isClinicContextResolvedCached = (): boolean =>
  cachedHasSelectedClinic || cachedClinicBetaFeaturesLoaded;

/**
 * Current user's email, cached outside Redux so dependency-free callers can apply
 * the internal 2026-UI launch override (see {@link usesEffectiveCip20Ui}). Kept
 * in sync by the auth slice whenever the user is set/cleared.
 */
let cachedUserEmail: string | null = null;

export const syncUserEmail = (email?: string | null): void => {
  cachedUserEmail = typeof email === "string" ? email : null;
};

// Mirrors subdomain.ts (isInternalCircadiaUser + isCipBetaOrLocalHost) WITHOUT
// importing it, keeping this module store-free for low-level components. The
// internal 2026-UI launch override: an internal Circadia employee
// (@circadia.health, non-pcc_) on a beta/local host.
const isInternalLaunchOverrideCached = (): boolean => {
  const email = (cachedUserEmail ?? "").trim().toLowerCase();
  const isInternal =
    email.endsWith("@circadia.health") &&
    !email.startsWith("pcc_") &&
    !email.includes("demo");
  if (!isInternal || typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host.startsWith("cipbeta.") ||
    host.startsWith("cip-beta.")
  );
};

// Mirrors `isCompassusSubdomain` (util/subdomain.ts) without importing it, so
// this module stays dependency-free for low-level components.
const isCompassusHost = (): boolean =>
  typeof window !== "undefined" &&
  (window.location.hostname === "compassus.circadia.link" ||
    window.location.hostname === "compassus.circadia.health");

/**
 * The effective 2026-UI decision for dependency-free callers: the `cip2.0-ui`
 * user∩clinic intersection OR the internal launch override (internal Circadia
 * employee on a beta/local host, applied only once the clinic context has
 * resolved), forced off in Compassus mode — the same answer as
 * `isUse2026UIEnabled`, without importing the store.
 */
export const usesEffectiveCip20Ui = (): boolean =>
  !cachedClinicIsCompassusMode &&
  !isCompassusHost() &&
  (usesCip20Ui() ||
    (isClinicContextResolvedCached() && isInternalLaunchOverrideCached()));
